import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { CheckSquare, Square, Camera, Trash2, ImageIcon, Loader2 } from "lucide-react";
import logger from "../lib/logger";

/**
 * CleaningChecklist
 *
 * For a given site, fetches the task templates that apply to its workplace
 * type and renders a checkbox + photo-upload row per task. Workers can:
 *  - Tick a task (creates a completion record without a photo)
 *  - Attach a photo (creates a completion record with the photo)
 *  - Undo (removes their completion)
 *
 * Completions reset each calendar day (server uses UTC date_key).
 */
export default function CleaningChecklist({ siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTask, setBusyTask] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/cleaning/tasks", { params: { site_id: siteId } });
      setData(data);
    } catch (err) {
      logger.error("Cleaning tasks load failed:", err);
      toast.error("Could not load cleaning tasks");
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (task_id, photoFile) => {
    setBusyTask(task_id);
    try {
      const form = new FormData();
      form.append("site_id", siteId);
      form.append("task_id", task_id);
      if (photoFile) form.append("photo", photoFile);
      await api.post("/cleaning/completions", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(photoFile ? "Logged with photo" : "Task ticked");
      load();
    } catch (err) {
      logger.error("Save completion failed:", err);
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusyTask(null);
    }
  };

  const undo = async (completionId) => {
    setBusyTask(completionId);
    try {
      await api.delete(`/cleaning/completions/${completionId}`);
      toast.success("Removed");
      load();
    } catch (err) {
      logger.error("Undo completion failed:", err);
      toast.error("Could not remove");
    } finally {
      setBusyTask(null);
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-300 bg-white p-5 rounded-sm flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading cleaning checklist…
      </div>
    );
  }
  if (!data) return null;

  if (!data.site.type) {
    return (
      <div className="border border-yellow-300 bg-yellow-50 p-4 rounded-sm text-sm text-yellow-900" data-testid="cleaning-no-type">
        No workplace type set for <b>{data.site.name}</b>. Ask your admin to choose one
        in the Sites tab to see the cleaning checklist for this site.
      </div>
    );
  }
  if (data.tasks.length === 0) {
    return (
      <div className="text-sm text-gray-500" data-testid="cleaning-no-tasks">
        No cleaning tasks defined for {data.site.type}.
      </div>
    );
  }

  const completionsByTask = {};
  for (const c of data.completions) {
    if (!completionsByTask[c.task_id]) completionsByTask[c.task_id] = [];
    completionsByTask[c.task_id].push(c);
  }

  return (
    <section className="border border-gray-300 bg-white rounded-sm" data-testid="cleaning-checklist">
      <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500">Cleaning checklist · {data.site.type}</div>
          <div className="font-black text-lg tracking-tight">{data.site.name}</div>
        </div>
        <div className="text-xs text-gray-500 font-mono">{data.date_key}</div>
      </header>
      <div className="divide-y divide-gray-100">
        {data.tasks.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            completions={completionsByTask[t.id] || []}
            busy={busyTask === t.id}
            onTick={() => submit(t.id, null)}
            onPhoto={(file) => submit(t.id, file)}
            onUndo={undo}
          />
        ))}
      </div>
    </section>
  );
}

function TaskRow({ task, completions, busy, onTick, onPhoto, onUndo }) {
  const inputRef = useRef(null);
  const done = completions.length > 0;
  const photoRequired = !!task.photo_required;
  const ctaLabel = done ? "Add another" : photoRequired ? "Photo (required)" : "Add photo";
  return (
    <div className="px-5 py-3 flex items-center gap-3 flex-wrap" data-testid={`cleaning-row-${task.id}`}>
      <button
        type="button"
        onClick={() => {
          if (done) return;
          if (photoRequired) {
            toast.error("This task requires a photo — use the camera button.");
            inputRef.current?.click();
            return;
          }
          onTick();
        }}
        disabled={busy || done}
        data-testid={`cleaning-tick-${task.id}`}
        className={`shrink-0 ${done ? "text-green-600" : "text-gray-400 hover:text-[#002FA7]"}`}
        aria-label="Mark complete"
      >
        {done ? <CheckSquare className="w-6 h-6" /> : <Square className="w-6 h-6" />}
      </button>

      <span className={`flex-1 min-w-[140px] text-sm font-medium ${done ? "line-through text-gray-500" : ""}`}>
        {task.label}
        {photoRequired && (
          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest font-bold rounded-sm align-middle">
            <Camera className="w-3 h-3" /> Photo
          </span>
        )}
      </span>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid={`cleaning-file-${task.id}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onPhoto(f);
        }}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`cleaning-photo-${task.id}`}
        className={`px-3 py-1.5 border text-xs font-bold rounded-sm flex items-center gap-1 ${
          photoRequired
            ? "border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
            : "border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]"
        }`}
      >
        <Camera className="w-3.5 h-3.5" /> {ctaLabel}
      </button>

      {/* Completion thumbnails / labels */}
      {completions.map((c) => (
        <CompletionChip key={c.id} completion={c} onUndo={onUndo} disabled={busy} />
      ))}
    </div>
  );
}

function CompletionChip({ completion, onUndo, disabled }) {
  const [src, setSrc] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    let blobUrl = null;
    if (completion.photo_path) {
      api.get(`/files/${completion.photo_path}`, { responseType: "blob" })
        .then((resp) => {
          if (!mounted) return;
          blobUrl = URL.createObjectURL(resp.data);
          setSrc(blobUrl);
        })
        .catch((err) => logger.error("Photo load failed:", err));
    }
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [completion.photo_path]);

  return (
    <>
      <div
        className="flex items-center gap-2 px-2 py-1 border border-green-300 bg-green-50 rounded-sm text-xs"
        data-testid={`completion-chip-${completion.id}`}
      >
        {completion.photo_path ? (
          src ? (
            <button onClick={() => setOpen(true)} className="block">
              <img src={src} alt="" className="w-8 h-8 object-cover rounded-sm" />
            </button>
          ) : (
            <ImageIcon className="w-4 h-4 text-gray-400" />
          )
        ) : (
          <span className="text-[10px] uppercase tracking-widest font-bold text-green-800">ticked</span>
        )}
        <span className="text-[10px] text-gray-600 font-mono whitespace-nowrap">
          {new Date(completion.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
        <button
          onClick={() => onUndo(completion.id)}
          disabled={disabled}
          className="text-red-600 hover:text-red-800"
          aria-label="Undo"
          data-testid={`undo-completion-${completion.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && src && (
        <div
          className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4"
          onClick={() => setOpen(false)}
          data-testid="photo-lightbox"
        >
          <img src={src} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </>
  );
}
